import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Descope, useSession } from '@descope/react-sdk';
import { Button, Spinner } from '@percihealth/react';
import styles from './DescopeLogin.module.css';

/**
 * Signs the user in with their Perci Platform Clinicians account.
 *
 * Descope runs the sign-in flow (password, MFA, passkeys) and establishes the
 * session, which is EBP's API credential. Establishing the Firebase
 * subscription session from it is left entirely to `FirebaseSessionSync` in
 * `DescopeSessionProvider` - the single owner of that exchange - so the login
 * page does not race it. Once the Firebase session comes up, the signed-in
 * user appears and this component navigates on.
 */
// A sign-in that has not produced a Firebase session after this long is stuck
// often enough (the token exchange failing, backend down) to offer a reload.
const SLOW_SIGN_IN_MS = 15_000;

// The Descope widget paints nothing until it has fetched the flow and its
// UI library from Descope, and nothing again once the flow has succeeded
// and the session is being exchanged with our API - so both waits used to
// look like an empty page under the "Sign in" title. Each gets a message.
type Phase =
  // the widget is on its way: its lazy chunk, the flow, DescopeUI
  | 'loading'
  // the form is on screen and the widget handles everything
  | 'ready'
  // the flow succeeded; the API is turning the session into a Firebase one
  | 'signing-in'
  // nothing to go on with except a reload
  | 'failed';

// A widget that has not reported ready after this long is stuck often
// enough (blocked script, Descope outage) to offer a reload beside the
// spinner - it keeps loading meanwhile.
const SLOW_LOAD_MS = 10_000;

const Waiting = ({ children }: { children: ReactNode }) => (
  <div className={styles.waiting} role="status">
    <span className={styles.spinner}>
      <Spinner />
    </span>
    <p className={styles.waitingText}>{children}</p>
  </div>
);

const ReloadButton = () => (
  <Button level="outline" onClick={() => window.location.reload()}>
    Reload the page
  </Button>
);

const DescopeFlow = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (phase !== 'loading') {
      return;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // While signing in, the Firebase session is being established by
  // FirebaseSessionSync; when it comes up the parent navigates away. If it
  // does not within the window, surface a reload rather than an endless
  // spinner.
  useEffect(() => {
    if (phase !== 'signing-in') {
      return;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_SIGN_IN_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Sign in</h1>
      {phase === 'loading' && (
        <>
          <Waiting>Loading the sign-in form...</Waiting>
          {slow && (
            <div className={styles.actions}>
              <p className={styles.hint}>
                This is taking longer than usual. Reloading the page usually
                helps.
              </p>
              <ReloadButton />
            </div>
          )}
        </>
      )}
      {phase === 'signing-in' && (
        <>
          <Waiting>Signing you in...</Waiting>
          {slow && (
            <div className={styles.actions}>
              <p className={styles.hint}>
                This is taking longer than usual. Reloading the page usually
                helps.
              </p>
              <ReloadButton />
            </div>
          )}
        </>
      )}
      {/* Mounted throughout, so the widget loads behind the spinner; hidden
          once the flow has succeeded so its closing screen does not show
          under the "Signing you in" message. */}
      <div hidden={phase === 'signing-in' || phase === 'failed'}>
        <Descope
          flowId={process.env.REACT_APP_DESCOPE_FLOW_ID ?? 'ebp-sign-in'}
          onReady={() => setPhase('ready')}
          onSuccess={() => {
            // The Descope flow established the session (EBP's API credential),
            // which is kept, not dropped. FirebaseSessionSync (the single owner
            // of the exchange) turns it into the Firebase subscription session;
            // when that signs in, the signed-in user appears and the parent
            // navigates on. Nothing to exchange here - doing so would race the
            // sync and both would try to create the Firebase user.
            setError(null);
            setSlow(false);
            setPhase('signing-in');
          }}
          onError={() => {
            setError('Sign-in failed. Please try again.');
            // With the form on screen the widget reports the error itself and
            // stays usable; before that there is nothing to retry on.
            setPhase((current) => (current === 'loading' ? 'failed' : current));
          }}
        />
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {phase === 'failed' && (
        <div className={styles.actions}>
          <ReloadButton />
        </div>
      )}
    </div>
  );
};

export default function DescopeLogin() {
  const { isAuthenticated, isSessionLoading } = useSession();

  // The Descope session is the credential, so it - not a possibly-stale
  // restored Firebase user - decides whether the user is signed in. Waiting
  // for it to settle first also avoids bouncing /login -> / -> /login when
  // Firebase restored a user whose Descope session has since expired.
  if (isSessionLoading) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>Sign in</h1>
        <Waiting>Checking your session...</Waiting>
      </div>
    );
  }

  if (isAuthenticated) {
    // PrivateElement parks the page the user was heading for on globalThis
    // before bouncing them here; send them back to it. Its own gate then waits
    // for the Firebase subscription session to catch up.
    const redirectAfterLogin = (globalThis as any)?.firstUrl ?? '/';
    return <Navigate to={redirectAfterLogin} />;
  }

  // The Descope SDK provider is mounted at the app root
  // (DescopeSessionProvider), so the flow uses the app-wide session rather
  // than one scoped to the login page.
  return <DescopeFlow />;
}
